/**
 * Модель экрана прав — Блок 2 · Ф8 «Права доступа» · В-Э.
 *
 * Чистые функции «ответ сервера → что показать и какое действие предложить».
 * Вынесены из компонентов, чтобы правило экрана читалось и проверялось без
 * отрисовки, а блок карточки и сводный список не держали по своей копии.
 *
 * ─── Что здесь НЕ решается ──────────────────────────────────────────────────
 *
 * «Откуда у человека право» считает сервер (`resolveRightState`), ответ несёт
 * уже посчитанное: `effect`, `effective`, `inertReason`, `inheritedDenyFrom`,
 * `viewMissing`. Экран их ПОКАЗЫВАЕТ. «Какое действие допустимо какому
 * получателю» — тоже не здесь: это `isEffectApplicable` из `@aris/shared`, то
 * же правило, по которому сервер отказывает в записи (фикс-пак 1).
 *
 * ─── ⚠️ Запретов нет (решение владельца 11.10.2026, Ф8 · В-У) ────────────────
 *
 * «Изначальные настройки отнимать не будем ни у кого. Только новые можно
 * отнимать». Экран НЕ ПРЕДЛАГАЕТ отнять ничего и никому: руководителю отдела —
 * «Добавить» и «Убрать», администратору — ничего (ему и добавлять нечего).
 *
 * Состояния `DENIED` и `INHERITED_DENY` при этом ОСТАЛИСЬ — но только как
 * ПОКАЗ строки `DENY`, которая лежит в базе с прежних времён либо записана
 * мимо сервиса. Сервер по такой строке по-прежнему отказывает (механизм не
 * вырезан), и молчать о ней экран не вправе: её показывают как есть и дают
 * сбросить. Штатным путём строка не появится — поставить её нечем.
 *
 * ⚠️ `byRole` — описание должности («по должности право есть»), а не ответ
 * «можно ли»: решать по нему доступ нельзя (шапка `user-rights.ts`). Здесь он
 * только печатается.
 */
import {
  AccessLevel,
  RIGHT_GRANT_SCOPE,
  RIGHT_GRANTABLE_TO_MANAGER,
  RIGHT_REQUIRES_VIEW,
  RightEffect,
  RightGrantScope,
  UserRight,
  UserStatus,
  isCrossProjectAdminRole,
  isEffectApplicable,
  viewRequiredBeforeAllow,
  type RightInertReason,
  type RightsPersonRef,
  type RightsUser,
  type UserRightLine,
  type UserRightsListResponse,
  type UserRightsResponse,
} from '@aris/shared';

// ─── Строка карточки ─────────────────────────────────────────────────────────

/**
 * Что стоит у человека по праву — одно из пяти.
 *
 *   ROLE           — поправки нет и право ведёт себя как по должности;
 *   GRANTED        — добавлено и ДЕЙСТВУЕТ;
 *   DENIED         — ⚠️ только показ: лежит действующая строка `DENY` прежних
 *                    времён. Поставить такую нельзя, сбросить — можно;
 *   INHERITED_DENY — ⚠️ только показ: закрыто такой же строкой у ГИПа, чьим
 *                    помощником человек служит; сбрасывается в карточке ГИПа;
 *   INERT          — НЕ ДЕЙСТВУЕТ: поправка стоит, но инертна («залипшая»
 *                    строка), либо «вести» осталось без парного «видеть»
 *                    (Р-195) — добавлено у руководителя без «видеть» или
 *                    положено администратору по должности при лежащем запрете
 *                    на «видеть».
 *
 * ⚠️ «Вести» без «видеть» — именно `INERT`, а не `GRANTED` / `ROLE`: зелёное
 * «Добавлено» над пояснением «не действует» читалось как «право есть».
 */
export type RightRowStatus = 'ROLE' | 'GRANTED' | 'DENIED' | 'INHERITED_DENY' | 'INERT';

/**
 * Действие строки. «Отнять» среди них нет и быть не может (11.10.2026).
 *
 *   ALLOW     — добавить руководителю отдела (`ALLOW`); журнал — с подтверждением;
 *   REMOVE    — убрать добавленное (`INHERIT`);
 *   RESET     — сбросить строку, которой штатно быть не должно (`INHERIT`):
 *               недействующую поправку либо лежащий запрет прежних времён;
 *   RESET_OWN — сбросить СОБСТВЕННУЮ поправку помощника под запретом ГИПа
 *               (`INHERIT`): запрет ГИПа этим не снимается.
 */
export type RightRowActionKind = 'ALLOW' | 'REMOVE' | 'RESET' | 'RESET_OWN';

export interface RightRowAction {
  readonly kind: RightRowActionKind;
  /** Что уйдёт в `PUT`. */
  readonly effect: RightEffect;
  /** Спросить перед записью. */
  readonly needsConfirm: boolean;
}

/** Почему действие не предложено. */
export type RightRowBlocked =
  /** «Вести договоры» и «архивировать» руководителю отдела не добавляются. */
  | { readonly kind: 'NOT_GRANTABLE' }
  /** «Вести» без «видеть» не действует (Р-195): сперва парное право. */
  | { readonly kind: 'NEEDS_VIEW'; readonly view: UserRight };

/** Где действует право, ДОБАВЛЕННОЕ руководителю отдела (Р-204, Р-217 а). */
export type RightRowScope = 'OBJECT' | 'CONTRACTS' | 'ORG';

export interface RightRowView {
  readonly right: UserRight;
  readonly byRole: boolean;
  /**
   * Оговорка к «по должности есть»: право по должности действует только на
   * своих объектах. Сегодня это «вести расходы» у ГИПа и его помощника —
   * расход пишет сквозной администратор либо тот, кто ведёт объект (Р-84,
   * `canWriteProject`).
   */
  readonly byRoleOwnObjectsOnly: boolean;
  readonly status: RightRowStatus;
  /** ГИП, чей запрет наследуется; только при `INHERITED_DENY`. */
  readonly inheritedFrom: RightsPersonRef | null;
  /** Что стоит в собственной строке человека. */
  readonly ownEffect: RightEffect;
  /** Действует ли собственная строка. */
  readonly ownEffective: boolean;
  readonly inertReason: RightInertReason | null;
  /** Парное «видеть», без которого это «вести» не действует; иначе `null`. */
  readonly viewMissing: UserRight | null;
  /** Область добавленного — только у руководителя отдела, иначе `null`. */
  readonly scope: RightRowScope | null;
  readonly action: RightRowAction | null;
  readonly blocked: RightRowBlocked | null;
  /** Версия строки для `PUT`; `null` — строки нет. */
  readonly version: number | null;
  /** Кто и когда менял строку последним; оба `null`, если строки нет. */
  readonly changedBy: RightsPersonRef | null;
  readonly changedAt: string | null;
}

function statusOf(line: UserRightLine): RightRowStatus {
  // Унаследованный запрет — первым: пока он стоит, право закрыто, что бы ни
  // было в собственной строке. Собственная строка показывается пояснением.
  if (line.inheritedDenyFrom !== null) return 'INHERITED_DENY';
  if (line.effect !== RightEffect.INHERIT && !line.effective) return 'INERT';
  if (line.effect === RightEffect.DENY) return 'DENIED';
  // Р-195: «вести» без «видеть» не действует — ни добавленное руководителю, ни
  // положенное администратору по должности.
  if (line.viewMissing) return 'INERT';
  return line.effect === RightEffect.ALLOW ? 'GRANTED' : 'ROLE';
}

function scopeOfRight(user: RightsUser, right: UserRight): RightRowScope | null {
  if (user.accessLevel !== AccessLevel.MANAGER) return null;
  if (!RIGHT_GRANTABLE_TO_MANAGER[right]) return null;
  if (RIGHT_GRANT_SCOPE[right] === RightGrantScope.ORG) return 'ORG';
  // Сумма договора привязана не к объекту, а к договорам объектов (Р-217 а).
  return right === UserRight.CONTRACT_SUM_READ ? 'CONTRACTS' : 'OBJECT';
}

/**
 * Добавлено ли и ДЕЙСТВУЕТ ли парное «видеть» — правило Р-195 у руководителя.
 * ⚠️ `effective` обязателен: «видеть», оставшееся от прежней должности, стоит в
 * строке как `ALLOW`, но ничего не открывает — и «вести» под ним не заработает.
 */
function hasEffectiveAllow(lines: readonly UserRightLine[], right: UserRight): boolean {
  const line = lines.find((l) => l.right === right);
  return line !== undefined && line.effect === RightEffect.ALLOW && line.effective;
}

function actionOf(
  user: RightsUser,
  line: UserRightLine,
  lines: readonly UserRightLine[],
): { action: RightRowAction | null; blocked: RightRowBlocked | null } {
  // Собственная строка с эффектом снимается всегда — и у не-получателя, и под
  // унаследованным запретом (решение плана П4: `INHERIT` принимается любому).
  if (line.effect !== RightEffect.INHERIT) {
    const inherit = { effect: RightEffect.INHERIT, needsConfirm: false } as const;
    if (line.inheritedDenyFrom !== null)
      return { action: { kind: 'RESET_OWN', ...inherit }, blocked: null };
    // Добавленное и действующее — «Убрать». Всё остальное — строка, которой
    // штатно быть не должно: недействующая поправка либо запрет прежних времён
    // (11.10.2026). Её не «возвращают», а сбрасывают — то же слово, что и у
    // залипшей строки.
    const removable = line.effect === RightEffect.ALLOW && line.effective;
    return { action: { kind: removable ? 'REMOVE' : 'RESET', ...inherit }, blocked: null };
  }

  // Строки нет. Не-получателю ставить нечего (Р-191; умолчание 3 Р-211).
  if (!user.eligible) return { action: null, blocked: null };

  // Единственное, что можно ПОСТАВИТЬ, — `ALLOW`: запрет не ставится никому
  // (`isEffectApplicable(DENY, …)` отвечает «нет» всегда). То же правило, по
  // которому сервер отвечает 422 (`@aris/shared`).
  if (isEffectApplicable(RightEffect.ALLOW, user.accessLevel, line.right)) {
    const view = viewRequiredBeforeAllow(user.accessLevel, line.right, (right) =>
      hasEffectiveAllow(lines, right),
    );
    if (view !== null) return { action: null, blocked: { kind: 'NEEDS_VIEW', view } };
    return {
      action: {
        kind: 'ALLOW',
        effect: RightEffect.ALLOW,
        // Журнал открывает всё и без фильтра (Р-190) — добавление подтверждается.
        needsConfirm: line.right === UserRight.AUDIT_READ,
      },
      blocked: null,
    };
  }

  // Руководителю отдела право не добавляется («вести договоры», Р-217 б;
  // «архивировать», решение владельца 10.10.2026) — причина названа в строке.
  // Администратору не ставится ничего, и объяснять это построчно незачем:
  // сказано один раз, в шапке блока.
  if (user.accessLevel === AccessLevel.MANAGER) {
    return { action: null, blocked: { kind: 'NOT_GRANTABLE' } };
  }
  return { action: null, blocked: null };
}

/** Одна строка карточки → что показать. `lines` — все восемь, для пар Р-195. */
export function describeRightRow(
  user: RightsUser,
  line: UserRightLine,
  lines: readonly UserRightLine[],
): RightRowView {
  const { action, blocked } = actionOf(user, line, lines);
  return {
    right: line.right,
    byRole: line.byRole,
    byRoleOwnObjectsOnly:
      line.byRole &&
      line.right === UserRight.EXPENSE_WRITE &&
      !isCrossProjectAdminRole(user.roleSlug),
    status: statusOf(line),
    inheritedFrom: line.inheritedDenyFrom,
    ownEffect: line.effect,
    ownEffective: line.effective,
    inertReason: line.inertReason,
    viewMissing: line.viewMissing ? (RIGHT_REQUIRES_VIEW[line.right] ?? null) : null,
    scope: scopeOfRight(user, line.right),
    action,
    blocked,
    version: line.version,
    changedBy: line.changedBy,
    changedAt: line.changedAt,
  };
}

// ─── Карточка целиком ────────────────────────────────────────────────────────

/**
 * Почему человеку права не выдаются. `PENDING` в КАРТОЧКЕ остаётся только у
 * приглашённого руководителя отдела — см. `describeRightsCard`.
 */
export type IneligibleReason = 'SUPERVISOR' | 'EMPLOYEE' | 'PENDING' | 'ARCHIVED';

/**
 * Причина «не получатель» — для объяснения в блоке; `null` у получателя.
 *
 * Уровень проверяется раньше статуса: сотруднику право не выдадут и после
 * принятия приглашения, и сказать ему «когда войдёт» значило бы пообещать
 * лишнее.
 */
export function ineligibleReasonOf(user: RightsUser): IneligibleReason | null {
  if (user.eligible) return null;
  if (user.accessLevel === AccessLevel.SUPERVISOR) return 'SUPERVISOR';
  if (user.accessLevel === AccessLevel.EMPLOYEE) return 'EMPLOYEE';
  if (user.status === UserStatus.PENDING) return 'PENDING';
  return 'ARCHIVED';
}

export interface RightsCardView {
  readonly ineligible: IneligibleReason | null;
  /**
   * Администратор, которому настраивать нечего (решение владельца 11.10.2026):
   * положенное по должности не отнимается, а добавить сверх неё нельзя. Блок
   * говорит это один раз и строк «По должности» не рисует. Сюда же —
   * ПРИГЛАШЁННЫЙ администратор: текст ему тот же, что действующему.
   */
  readonly fixedByRole: boolean;
  /**
   * Строки к показу:
   *   · руководитель отдела — все восемь;
   *   · администратор и не-получатель — только те, где что-то СТОИТ (строка с
   *     эффектом, запрет ГИПа, «вести» под запретом на «видеть»): её есть чем
   *     снять либо о ней надо знать. Остальным показывать нечего.
   */
  readonly rows: readonly RightRowView[];
  /**
   * Что даёт должность — одним значением на блок; `null` — у строк оно разное
   * (сегодня сервер ставит его по уровню, то есть одинаково) и печатается
   * построчно.
   */
  readonly byRole: boolean | null;
}

export function describeRightsCard(card: UserRightsResponse): RightsCardView {
  const reason = ineligibleReasonOf(card.user);
  // Приглашённый администратор показывается как действующий (Ф8 · В-У,
  // фикс-пак 1): «права можно будет добавить, когда войдёт» ему — неправда,
  // добавлять нечего ни сейчас, ни потом. Это только ПОКАЗ: `user.eligible` у
  // него по-прежнему «нет», и поставить ему `actionOf` ничего не предложит.
  const ineligible =
    reason === 'PENDING' && card.user.accessLevel === AccessLevel.ADMIN ? null : reason;
  const fixedByRole = ineligible === null && card.user.accessLevel === AccessLevel.ADMIN;
  const all = card.rights.map((line) => describeRightRow(card.user, line, card.rights));
  const rows =
    ineligible === null && !fixedByRole
      ? all
      : all.filter((row) =>
          ineligible !== null ? row.ownEffect !== RightEffect.INHERIT : row.status !== 'ROLE',
        );
  const first = card.rights[0]?.byRole ?? null;
  return {
    ineligible,
    fixedByRole,
    rows,
    byRole: first !== null && card.rights.every((line) => line.byRole === first) ? first : null,
  };
}

// ─── Сводный список ──────────────────────────────────────────────────────────

export type RightsListItem = UserRightsListResponse['items'][number];

/** Одна поправка в строке сводного списка. */
export interface RightsListChip {
  readonly right: UserRight;
  /**
   * `DENIED` и `INHERITED` — ⚠️ только показ строки `DENY` прежних времён
   * (своей либо ГИПа): поставить такую нельзя (11.10.2026), а не показать —
   * значит оставить действующий запрет, который нечем найти.
   */
  readonly kind: 'DENIED' | 'GRANTED' | 'INERT' | 'INHERITED';
  /** Почему не действует; только у `INERT` с инертной строкой. */
  readonly inertReason: RightInertReason | null;
  /** Парное «видеть», без которого «вести» не действует (Р-195); только у `INERT`. */
  readonly viewMissing: UserRight | null;
  /** Где действует добавленное; только у `GRANTED`, и только объектное. */
  readonly scope: 'OBJECT' | 'CONTRACTS' | null;
}

export interface RightsListItemView {
  readonly user: RightsUser;
  readonly gip: RightsPersonRef | null;
  readonly chips: readonly RightsListChip[];
}

/** Строка сводного списка → что показать. Порядок значков — как в ответе. */
export function describeRightsListItem(item: RightsListItem): RightsListItemView {
  const chips: RightsListChip[] = item.overrides.map((o) => {
    if (!o.effective) {
      return {
        right: o.right,
        kind: 'INERT',
        inertReason: o.inertReason,
        viewMissing: null,
        scope: null,
      };
    }
    if (o.effect === RightEffect.DENY) {
      return { right: o.right, kind: 'DENIED', inertReason: null, viewMissing: null, scope: null };
    }
    // Р-195: «вести», оставшееся без «видеть», — не «Добавлено».
    if (o.viewMissing) {
      return {
        right: o.right,
        kind: 'INERT',
        inertReason: null,
        viewMissing: RIGHT_REQUIRES_VIEW[o.right] ?? null,
        scope: null,
      };
    }
    const scope = scopeOfRight(item.user, o.right);
    return {
      right: o.right,
      kind: 'GRANTED',
      inertReason: null,
      viewMissing: null,
      scope: scope === 'ORG' ? null : scope,
    };
  });
  for (const right of item.inheritedDenies) {
    chips.push({ right, kind: 'INHERITED', inertReason: null, viewMissing: null, scope: null });
  }
  return { user: item.user, gip: item.gip, chips };
}

/** Отбор сводного списка по имени и по праву; пустые условия не сужают. */
export function filterRightsList(
  items: readonly RightsListItemView[],
  filter: { readonly search: string; readonly right: UserRight | null },
): readonly RightsListItemView[] {
  const needle = filter.search.trim().toLocaleLowerCase('ru');
  return items.filter((item) => {
    if (needle.length > 0 && !item.user.fullName.toLocaleLowerCase('ru').includes(needle)) {
      return false;
    }
    if (filter.right !== null && !item.chips.some((chip) => chip.right === filter.right)) {
      return false;
    }
    return true;
  });
}
